import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { OTAVehicle, OTAShadow } from "../../types";
import { otaVehiclesApi } from "../../api";

// ─── Helpers ────────────────────────────────────────────────────────────────

function getHostVersion(shadow: OTAShadow): string | null {
  const compStatus = shadow.component_status as Record<string, unknown> | null;
  if (compStatus && compStatus.host) {
    const h = compStatus.host as Record<string, unknown>;
    if (typeof h.current === "string") return h.current;
  }
  const host = shadow.host as Record<string, unknown> | null;
  if (host && typeof host.current === "string") return host.current;
  return null;
}

// ─── Styled Components ───────────────────────────────────────────────────────

const Panel = styled.div`
  background: #1e2433;
  border: 1px solid #2d3748;
  border-radius: 8px;
  padding: 16px 20px;
  margin: 4px 0 8px 0;
`;

const PanelTitle = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #64748b;
  margin-bottom: 14px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  padding: 3px 0;
  color: #cbd5e1;
`;

const Label = styled.span`
  color: #64748b;
  min-width: 140px;
`;

const StatusBadge = styled.span<{ ok: boolean }>`
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  background: ${({ ok }) =>
    ok ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)"};
  color: ${({ ok }) => (ok ? "#22c55e" : "#ef4444")};
`;

const Divider = styled.div`
  border-top: 1px solid #2d3748;
  margin: 12px 0;
`;

const RawToggle = styled.button`
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 11px;
  cursor: pointer;
  padding: 0;
  margin-bottom: 8px;
  &:hover {
    text-decoration: underline;
  }
`;

const RawJson = styled.pre`
  background: #0f1520;
  border: 1px solid #1e2d3d;
  border-radius: 6px;
  padding: 12px;
  font-size: 10px;
  color: #94a3b8;
  overflow: auto;
  max-height: 320px;
  white-space: pre;
  font-family: monospace;
`;

const Muted = styled.div`
  font-size: 12px;
  color: #4b5563;
  font-style: italic;
`;

const RefreshBtn = styled.button`
  margin-top: 10px;
  background: none;
  border: none;
  color: #3b82f6;
  font-size: 11px;
  cursor: pointer;
  padding: 0;
  &:hover {
    text-decoration: underline;
  }
`;

// ─── Component ───────────────────────────────────────────────────────────────

interface Props {
  vehicle: OTAVehicle;
  onShadowLoaded?: () => void;
}

const ShadowStatusPanel: React.FC<Props> = ({ vehicle, onShadowLoaded }) => {
  const [shadow, setShadow] = useState<OTAShadow | null>(null);
  const [loading, setLoading] = useState(true);
  const [showRaw, setShowRaw] = useState(false);

  const fetchShadow = async () => {
    setLoading(true);
    try {
      const data = await otaVehiclesApi.getShadow(vehicle.id);
      setShadow(data);
      if (data !== null) onShadowLoaded?.();
    } catch {
      setShadow(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShadow();
  }, [vehicle.id]);

  const installStatus = shadow?.installation_status ?? null;
  const isOk = installStatus === "ok" || installStatus === "success";
  const hostVersion = shadow ? getHostVersion(shadow) : null;
  const sessionId = shadow?.session_id ?? null;
  const timestamp = shadow?.installation_timestamp ?? null;

  return (
    <Panel>
      <PanelTitle>Installed State</PanelTitle>

      {loading ? (
        <Muted>Loading…</Muted>
      ) : shadow === null ? (
        <Muted>
          No state reported yet — vehicle has not started Greengrass.
        </Muted>
      ) : (
        <>
          {installStatus && (
            <Row>
              <Label>Installation status</Label>
              <StatusBadge ok={isOk}>{installStatus}</StatusBadge>
            </Row>
          )}
          {hostVersion && (
            <Row>
              <Label>Host firmware</Label>
              <span>{hostVersion}</span>
            </Row>
          )}
          {sessionId && (
            <Row>
              <Label>Session ID</Label>
              <span style={{ fontFamily: "monospace", fontSize: 10 }}>
                {sessionId}
              </span>
            </Row>
          )}
          {timestamp && (
            <Row>
              <Label>Timestamp</Label>
              <span>{new Date(timestamp).toLocaleString()}</span>
            </Row>
          )}

          <Divider />
          <RawToggle onClick={() => setShowRaw((v) => !v)}>
            {showRaw ? "▲ Hide raw shadow" : "▼ Show raw shadow"}
          </RawToggle>
          {showRaw && <RawJson>{JSON.stringify(shadow, null, 2)}</RawJson>}
        </>
      )}

      {!loading && <RefreshBtn onClick={fetchShadow}>↻ Refresh</RefreshBtn>}
    </Panel>
  );
};

export default ShadowStatusPanel;
